import { useTranslation } from 'react-i18next';
import { Typography, Tabs, Table } from 'antd';
import styles from './guide.module.scss';

const { Title, Paragraph, Text } = Typography;

const CodeBlock = ({ code }) => (
  <pre className={styles.codeBlock}>
    <code>{code}</code>
  </pre>
);

const API_BASE = 'https://api.breezedeus.com';

const CODE_SNIPPETS = {
  quickStart: {
    python: `import requests

url = '${API_BASE}/pix2text'
headers = {
    'X-API-Key': 'p2t_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'
}
data = {
    'language': 'Simplified Chinese',
    'file_type': 'text_formula',
    'server_type': 'pro',
}
files = {
    'image': ('example.jpg', open('example.jpg', 'rb'))
}

response = requests.post(url, headers=headers, data=data, files=files)
print(response.json())`,
    curl: `curl -X POST \\
  -H "X-API-Key: p2t_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" \\
  -F "image=@example.jpg" \\
  -F "language=Simplified Chinese" \\
  -F "file_type=text_formula" \\
  -F "server_type=pro" \\
  ${API_BASE}/pix2text`,
    javascript: `const axios = require('axios');
const FormData = require('form-data');
const fs = require('fs');

const formData = new FormData();
formData.append('image', fs.createReadStream('example.jpg'));
formData.append('language', 'Simplified Chinese');
formData.append('file_type', 'text_formula');
formData.append('server_type', 'pro');

const response = await axios.post('${API_BASE}/pix2text', formData, {
  headers: {
    'X-API-Key': 'p2t_live_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    ...formData.getHeaders()
  }
});
console.log(response.data);`,
  },
  submitTask: {
    python: `import requests

url = '${API_BASE}/pix2text'
headers = {'X-API-Key': 'p2t_live_xxxx...'}
data = {
    'language': 'Simplified Chinese',
    'file_type': 'text_formula',
    'server_type': 'pro',
}
files = {'image': ('example.jpg', open('example.jpg', 'rb'))}

r = requests.post(url, headers=headers, data=data, files=files)
result = r.json()
print(result)
# {'task_id': 'xxx-xxx-xxx', 'status_code': 200, ...}`,
    curl: `curl -X POST \\
  -H "X-API-Key: p2t_live_xxxx..." \\
  -F "image=@example.jpg" \\
  -F "language=Simplified Chinese" \\
  -F "file_type=text_formula" \\
  -F "server_type=pro" \\
  ${API_BASE}/pix2text`,
    javascript: `const formData = new FormData();
formData.append('image', fs.createReadStream('example.jpg'));
formData.append('language', 'Simplified Chinese');
formData.append('file_type', 'text_formula');
formData.append('server_type', 'pro');

const response = await axios.post('${API_BASE}/pix2text', formData, {
  headers: { 'X-API-Key': 'p2t_live_xxxx...', ...formData.getHeaders() }
});
const taskId = response.data.task_id;`,
  },
  submitPdfTask: {
    python: `import requests

url = '${API_BASE}/pix2text'
headers = {'X-API-Key': 'p2t_live_xxxx...'}
data = {
    'language': 'Simplified Chinese',
    'file_type': 'text_formula',
    'server_type': 'ultra',  # PDF recognition requires Ultra model
}
files = {'image': ('document.pdf', open('document.pdf', 'rb'))}

r = requests.post(url, headers=headers, data=data, files=files)
result = r.json()
print(result)
# {'task_id': 'xxx-xxx-xxx', 'status_code': 200, ...}`,
    curl: `# PDF recognition requires server_type=ultra
curl -X POST \\
  -H "X-API-Key: p2t_live_xxxx..." \\
  -F "image=@document.pdf" \\
  -F "language=Simplified Chinese" \\
  -F "file_type=text_formula" \\
  -F "server_type=ultra" \\
  ${API_BASE}/pix2text`,
    javascript: `const formData = new FormData();
formData.append('image', fs.createReadStream('document.pdf'));
formData.append('language', 'Simplified Chinese');
formData.append('file_type', 'text_formula');
formData.append('server_type', 'ultra'); // PDF recognition requires Ultra model

const response = await axios.post('${API_BASE}/pix2text', formData, {
  headers: { 'X-API-Key': 'p2t_live_xxxx...', ...formData.getHeaders() }
});
const taskId = response.data.task_id;`,
  },
  getResult: {
    python: `import requests
import time

task_id = 'xxx-xxx-xxx'
url = f'${API_BASE}/result/{task_id}'
headers = {'X-API-Key': 'p2t_live_xxxx...'}

while True:
    r = requests.get(url, headers=headers)
    result = r.json()
    if result.get('status') == 'FINISHED':
        print(result['results'])
        break
    time.sleep(2)  # poll every 2 seconds`,
    curl: `# Poll until status is FINISHED
curl -X GET \\
  -H "X-API-Key: p2t_live_xxxx..." \\
  ${API_BASE}/result/xxx-xxx-xxx`,
    javascript: `const taskId = 'xxx-xxx-xxx';
const headers = { 'X-API-Key': 'p2t_live_xxxx...' };

let result;
while (true) {
  const res = await axios.get(\`${API_BASE}/result/\${taskId}\`, { headers });
  if (res.data.status === 'FINISHED') {
    result = res.data.results;
    break;
  }
  await new Promise(r => setTimeout(r, 2000)); // poll every 2s
}`,
  },
  export: {
    python: `import requests

task_id = 'xxx-xxx-xxx'
url = f'${API_BASE}/result/{task_id}/export'
headers = {'X-API-Key': 'p2t_live_xxxx...'}
params = {'format': 'docx'}  # or 'pdf'

response = requests.get(url, headers=headers, params=params)
with open('result.docx', 'wb') as f:
    f.write(response.content)`,
    curl: `curl -X GET \\
  -H "X-API-Key: p2t_live_xxxx..." \\
  "${API_BASE}/result/xxx-xxx-xxx/export?format=docx" \\
  --output result.docx`,
    javascript: `const response = await axios.get(
  \`${API_BASE}/result/\${taskId}/export\`,
  {
    headers: { 'X-API-Key': 'p2t_live_xxxx...' },
    params: { format: 'docx' },
    responseType: 'arraybuffer'
  }
);
fs.writeFileSync('result.docx', Buffer.from(response.data));`,
  },
};

export default function ApiKeyGuide() {
  const { t } = useTranslation();

  const langTabs = (snippets) => [
    { key: 'python', label: 'Python', children: <CodeBlock code={snippets.python} /> },
    { key: 'curl', label: 'cURL', children: <CodeBlock code={snippets.curl} /> },
    { key: 'javascript', label: 'JavaScript', children: <CodeBlock code={snippets.javascript} /> },
  ];

  const errorColumns = [
    { title: t('apiKeys.guideErrorCode'), dataIndex: 'code', key: 'code', width: 120 },
    { title: t('apiKeys.guideErrorMeaning'), dataIndex: 'desc', key: 'desc' },
  ];
  const errorData = [
    { key: '400', code: '400', desc: t('apiKeys.guideError400') },
    { key: '401', code: '401', desc: t('apiKeys.guideError401') },
    { key: '403', code: '403', desc: t('apiKeys.guideError403') },
    { key: '500', code: '500', desc: t('apiKeys.guideError500') },
  ];

  const paramColumns = [
    { title: t('apiKeys.guideParamName'), dataIndex: 'name', key: 'name', width: 150 },
    { title: t('apiKeys.guideParamRequired'), dataIndex: 'required', key: 'required', width: 80 },
    { title: t('apiKeys.guideParamDefault'), dataIndex: 'defaultVal', key: 'defaultVal', width: 150 },
    { title: t('apiKeys.guideParamDescription'), dataIndex: 'desc', key: 'desc' },
  ];
  const paramData = [
    { key: 'image', name: 'image', required: t('apiKeys.guideParamYes'), defaultVal: '-', desc: t('apiKeys.guideParamImageDesc') },
    { key: 'language', name: 'language', required: t('apiKeys.guideParamNo'), defaultVal: 'Simplified Chinese', desc: t('apiKeys.guideParamLanguageDesc') },
    { key: 'file_type', name: 'file_type', required: t('apiKeys.guideParamNo'), defaultVal: 'text_formula', desc: t('apiKeys.guideParamFileTypeDesc') },
    { key: 'server_type', name: 'server_type', required: t('apiKeys.guideParamNo'), defaultVal: 'pro', desc: t('apiKeys.guideParamServerTypeDesc') },
    { key: 'resized_shape', name: 'resized_shape', required: t('apiKeys.guideParamNo'), defaultVal: '608', desc: t('apiKeys.guideParamResizedShapeDesc') },
    { key: 'embed_sep', name: 'embed_sep', required: t('apiKeys.guideParamNo'), defaultVal: ' $,$ ', desc: t('apiKeys.guideParamEmbedSepDesc') },
    { key: 'isolated_sep', name: 'isolated_sep', required: t('apiKeys.guideParamNo'), defaultVal: '$$\\n, \\n$$', desc: t('apiKeys.guideParamIsolatedSepDesc') },
  ];

  return (
    <div className={styles.guide}>
      {/* Quick Start */}
      <section className={styles.section}>
        <Title level={4}>{t('apiKeys.guideQuickStartTitle')}</Title>
        <Paragraph type="secondary">{t('apiKeys.guideQuickStartDesc')}</Paragraph>
        <Tabs items={langTabs(CODE_SNIPPETS.quickStart)} size="small" />
      </section>

      {/* OCR Flow */}
      <section className={styles.section}>
        <Title level={4}>{t('apiKeys.guideOcrFlowTitle')}</Title>
        <Paragraph type="secondary">{t('apiKeys.guideOcrFlowDesc')}</Paragraph>

        <Title level={5}>{t('apiKeys.guideSubmitTask')}</Title>
        <Paragraph type="secondary">{t('apiKeys.guideSubmitTaskDesc')}</Paragraph>
        <Tabs items={langTabs(CODE_SNIPPETS.submitTask)} size="small" />

        <Title level={5}>{t('apiKeys.guideSubmitPdfTask')}</Title>
        <Paragraph type="secondary">{t('apiKeys.guideSubmitPdfTaskDesc')}</Paragraph>
        <Tabs items={langTabs(CODE_SNIPPETS.submitPdfTask)} size="small" />

        <Title level={5}>{t('apiKeys.guideGetResult')}</Title>
        <Paragraph type="secondary">{t('apiKeys.guideGetResultDesc')}</Paragraph>
        <Tabs items={langTabs(CODE_SNIPPETS.getResult)} size="small" />
      </section>

      {/* Export */}
      <section className={styles.section}>
        <Title level={4}>{t('apiKeys.guideExportTitle')}</Title>
        <Paragraph type="secondary">{t('apiKeys.guideExportDesc')}</Paragraph>
        <Tabs items={langTabs(CODE_SNIPPETS.export)} size="small" />
      </section>

      {/* Parameters */}
      <section className={styles.section}>
        <Title level={4}>{t('apiKeys.guideParamsTitle')}</Title>
        <Paragraph type="secondary">{t('apiKeys.guideParamsDesc')}</Paragraph>
        <Table
          columns={paramColumns}
          dataSource={paramData}
          pagination={false}
          size="small"
          bordered
        />
      </section>

      {/* Error Codes */}
      <section className={styles.section}>
        <Title level={4}>{t('apiKeys.guideErrorTitle')}</Title>
        <Paragraph type="secondary">{t('apiKeys.guideErrorDesc')}</Paragraph>
        <Table
          columns={errorColumns}
          dataSource={errorData}
          pagination={false}
          size="small"
          bordered
        />
      </section>
    </div>
  );
}
